/**
 * 관리자 화면: 사무소 목록, 사무소 하나, 사용 현황, 텔레그램 (기반 공사 6단계)
 * 사무소 업무 자료(인력, 거래처, 현장, 돈)는 서버가 처음부터 주지 않는다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type Reply } from '../api.ts';
import { Button, Check, Field, Message } from '../ui.tsx';

type Lost = () => void;

/** 관리자 출입증이 끝났으면(401) 로그인 화면으로 */
function useLoad<T>(url: string | null, lost: Lost): { data: T | null; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!url) return;
    let live = true;
    void api<T>('GET', url).then((r: Reply<T>) => {
      if (!live) return;
      if (r.ok) {
        setData(r);
        setError(null);
      } else if (r.status === 401) lost();
      else setError(r.message);
    });
    return () => {
      live = false;
    };
  }, [url, n, lost]);
  return { data, error, reload: useCallback(() => setN((x) => x + 1), []) };
}

const STATUSES = ['시작 전', '체험 중', '이용 중', '결제 실패', '이용 막힘', '해지 예약', '해지됨'];

interface OfficeRow {
  office_id: string;
  name: string | null;
  biz_no: string;
  service_status: string;
  plan: string;
  created_at: string;
  last_used_on: string | null;
  biz_status: string | null;
  biz_checked_at: string | null;
  is_test: boolean;
}

function bizLabel(o: { biz_status: string | null }): { text: string; warn: boolean } {
  if (o.biz_status === null) return { text: '확인 대기', warn: true };
  return { text: o.biz_status, warn: o.biz_status !== '계속' };
}

function bizNoText(b: string): string {
  return b.length === 10 ? `${b.slice(0, 3)}-${b.slice(3, 5)}-${b.slice(5)}` : b;
}

const day = (v: string | null | undefined): string => (v ? v.slice(0, 10) : '');

function useHideTest(): [boolean, (v: boolean) => void] {
  const [hide, setHide] = useState(() => {
    try {
      return window.localStorage.getItem('admin_hide_test') !== '0';
    } catch {
      return true;
    }
  });
  const set = (v: boolean) => {
    setHide(v);
    try {
      window.localStorage.setItem('admin_hide_test', v ? '1' : '0');
    } catch {
      // 기억하지 못해도 화면은 그대로 쓴다
    }
  };
  return [hide, set];
}

export function OfficesPage({ go, lost }: { go: (p: string) => void; lost: Lost }) {
  const { data, error } = useLoad<{ offices: OfficeRow[] }>('/api/admin/offices', lost);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [hideTest, setHideTest] = useHideTest();
  const rows = useMemo(() => {
    const word = q.replace(/[-\s]/g, '').toLowerCase();
    return (data?.offices ?? []).filter((o) =>
      (!hideTest || !o.is_test) &&
      (!status || o.service_status === status) &&
      (!word || o.biz_no.includes(word) || (o.name ?? '').replace(/\s/g, '').toLowerCase().includes(word))
    );
  }, [data, q, status, hideTest]);
  return (
    <>
      <h1>사무소</h1>
      <div className="row">
        <input type="search" placeholder="상호나 사업자번호" value={q} onChange={(e) => setQ(e.target.value)} aria-label="찾기" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="상태">
          <option value="">상태 전체</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <Check label="시험 사무소 숨기기" checked={hideTest} onChange={setHideTest} />
      <Message text={error} />
      {data && <p className="muted">{rows.length}곳</p>}
      {data && rows.length === 0 && <p>보여 줄 사무소가 없어요.</p>}
      {rows.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr><th>상호</th><th>사업자번호</th><th>상태</th><th>요금제</th><th>가입한 날</th><th>마지막으로 쓴 날</th><th>국세청</th></tr>
          </thead>
          <tbody>
            {rows.map((o) => {
              const biz = bizLabel(o);
              return (
                <tr key={o.office_id} className="click" onClick={() => go(`/admin/offices/${o.office_id}`)}>
                  <td data-label="상호">{o.name ?? <span className="muted">(아직 안 적음)</span>}{o.is_test && <span className="muted"> 시험</span>}</td>
                  <td data-label="사업자번호">{bizNoText(o.biz_no)}</td>
                  <td data-label="상태">{o.service_status}</td>
                  <td data-label="요금제">{o.plan}</td>
                  <td data-label="가입한 날">{day(o.created_at)}</td>
                  <td data-label="마지막으로 쓴 날">{o.last_used_on ?? <span className="muted">없음</span>}</td>
                  <td data-label="국세청" className={biz.warn ? 'warn' : undefined}>{biz.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

interface OfficeDetail {
  office_id: string;
  name: string | null;
  ceo_name: string | null;
  biz_no: string;
  license_no: string;
  phone: string | null;
  email: string | null;
  biz_status: string | null;
  biz_checked_at: string | null;
  plan: string;
  service_status: string;
  plan_next: string | null;
  trial_until: string | null;
  billing_day: number | null;
  grace_until: string | null;
  created_at: string;
  owner_count: number;
  staff_count: number;
  staff_off_count: number;
  live_sessions: number;
  last_used_on: string | null;
}

interface Payment {
  payment_id: string;
  kind: string;
  period_from: string | null;
  period_to: string | null;
  plan: string | null;
  amount: number;
  vat: number;
  result: string;
  fail_reason: string | null;
  paid_at: string | null;
}

interface LoginRow {
  role: string;
  display_name: string;
  login_id: string | null;
  has_kakao: boolean;
  has_naver: boolean;
  status: string;
}

const won = (n: number): string => `${n.toLocaleString('ko-KR')}원`;

/** 바꾸기 한 가지(날짜나 이메일 + 사유). 사유가 없으면 저장하지 않는다. */
function ChangeBox(props: { title: string; kind: 'date' | 'email'; label: string; onSave: (value: string, reason: string) => Promise<string | null> }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  if (!open) return <Button onClick={() => setOpen(true)}>{props.title}</Button>;
  return (
    <div className="box">
      <h2>{props.title}</h2>
      {props.kind === 'date' ? (
        <label className="row">{props.label} <input type="date" value={value} onChange={(e) => setValue(e.target.value)} /></label>
      ) : (
        <Field label={props.label} type="email" value={value} onChange={setValue} />
      )}
      <Field label="사유 (꼭 적어 주세요)" value={reason} onChange={setReason} />
      <Message text={error} />
      <Button primary disabled={!value || reason.trim().length < 2} onClick={() => void props.onSave(value, reason).then((e) => (e ? setError(e) : setOpen(false)))}>저장</Button>
      <Button onClick={() => setOpen(false)}>닫기</Button>
    </div>
  );
}

export function OfficePage({ id, go, lost }: { id: string; go: (p: string) => void; lost: Lost }) {
  const { data, error, reload } = useLoad<{ office: OfficeDetail; payments: Payment[] }>(`/api/admin/offices/${id}`, lost);
  const [logins, setLogins] = useState<LoginRow[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const o = data?.office;

  const save = (path: string, body: Record<string, unknown>, done: string) => async (): Promise<string | null> => {
    const r = await api('POST', `/api/admin/offices/${id}/${path}`, body);
    if (!r.ok) return r.status === 401 ? (lost(), null) : r.message;
    setNote(done);
    reload();
    return null;
  };

  const showLogins = async () => {
    const r = await api<{ logins: LoginRow[] }>('POST', `/api/admin/offices/${id}/logins`);
    if (r.ok) setLogins(r.logins);
    else if (r.status === 401) lost();
  };

  if (!o) return <><Button onClick={() => go('/admin/offices')}>목록으로</Button><Message text={error} /></>;
  const biz = bizLabel(o);
  return (
    <>
      <Button onClick={() => go('/admin/offices')}>목록으로</Button>
      <h1>{o.name ?? '(상호 아직 안 적음)'}</h1>
      <Message text={note} kind="info" />
      <section className="box">
        <h2>기본</h2>
        <dl className="kv">
          <dt>대표자</dt><dd>{o.ceo_name ?? '-'}</dd>
          <dt>사업자번호</dt><dd>{bizNoText(o.biz_no)}</dd>
          <dt>직업소개사업 등록번호</dt><dd>{o.license_no}</dd>
          <dt>사무소 전화</dt><dd>{o.phone ?? '-'}</dd>
          <dt>이메일</dt><dd>{o.email ?? '-'}</dd>
          <dt>국세청 상태</dt><dd className={biz.warn ? 'warn' : undefined}>{biz.text}{o.biz_checked_at && <span className="muted"> ({day(o.biz_checked_at)} 조회)</span>}</dd>
          <dt>가입한 날</dt><dd>{day(o.created_at)}</dd>
          <dt>마지막으로 쓴 날</dt><dd>{o.last_used_on ?? '없음'}</dd>
        </dl>
      </section>
      <section className="box">
        <h2>요금제</h2>
        <dl className="kv">
          <dt>요금제</dt><dd>{o.plan}</dd>
          <dt>상태</dt><dd>{o.service_status}{o.grace_until && o.service_status === '이용 막힘' && <span> ({o.grace_until}까지 쓰게 함)</span>}</dd>
          <dt>다음 결제</dt><dd>{o.plan_next ?? '-'}</dd>
          <dt>체험 끝나는 날</dt><dd>{o.trial_until ?? '-'}</dd>
          <dt>결제일</dt><dd>{o.billing_day ? `매달 ${o.billing_day}일` : '-'}</dd>
        </dl>
        {data.payments.length === 0 ? <p className="muted">결제 기록이 없어요.</p> : (
          <table className="admin-table">
            <thead><tr><th>날짜</th><th>종류</th><th>기간</th><th>요금제</th><th>금액</th><th>결과</th></tr></thead>
            <tbody>
              {data.payments.map((p) => (
                <tr key={p.payment_id}>
                  <td data-label="날짜">{day(p.paid_at)}</td>
                  <td data-label="종류">{p.kind}</td>
                  <td data-label="기간">{p.period_from ? `${p.period_from} ~ ${p.period_to ?? ''}` : '-'}</td>
                  <td data-label="요금제">{p.plan ?? '-'}</td>
                  <td data-label="금액">{won(p.amount + p.vat)}</td>
                  <td data-label="결과" className={p.result === '실패' ? 'warn' : undefined}>{p.result}{p.fail_reason ? ` (${p.fail_reason})` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="row">
          {o.service_status === '체험 중' && (
            <ChangeBox title="체험 기간 늘리기" kind="date" label="새로 끝나는 날" onSave={(v, reason) => save('trial', { until: v, reason }, '체험 기간을 늘렸어요.')()} />
          )}
          {o.service_status === '이용 막힘' && (
            <ChangeBox title="정한 날까지 쓰게 하기" kind="date" label="이 날까지" onSave={(v, reason) => save('grace', { until: v, reason }, '정한 날까지 쓰게 했어요.')()} />
          )}
        </div>
      </section>
      <section className="box">
        <h2>계정</h2>
        <dl className="kv">
          <dt>소장님</dt><dd>{o.owner_count}</dd>
          <dt>직원</dt><dd>{o.staff_count}명{o.staff_off_count > 0 && <span className="muted"> (사용 안 함 {o.staff_off_count}명)</span>}</dd>
          <dt>지금 로그인된 곳</dt><dd>{o.live_sessions}곳</dd>
        </dl>
        {logins ? (
          <table className="admin-table">
            <thead><tr><th>구분</th><th>이름</th><th>아이디</th><th>카카오</th><th>네이버</th><th>상태</th></tr></thead>
            <tbody>
              {logins.map((l, i) => (
                <tr key={i}>
                  <td data-label="구분">{l.role}</td>
                  <td data-label="이름">{l.display_name}</td>
                  <td data-label="아이디">{l.login_id ?? '-'}</td>
                  <td data-label="카카오">{l.has_kakao ? '연결됨' : '-'}</td>
                  <td data-label="네이버">{l.has_naver ? '연결됨' : '-'}</td>
                  <td data-label="상태">{l.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Button onClick={() => void showLogins()}>아이디 보기</Button>
        )}
        <div className="row">
          <ChangeBox title="이메일 바꾸기" kind="email" label="새 이메일" onSave={(v, reason) => save('email', { email: v, reason }, '이메일을 바꿨어요.')()} />
        </div>
      </section>
    </>
  );
}

interface UsageReport {
  screens: { screen: string; opens: number; avg_seconds: number | null }[];
  buttons: { screen: string; target: string; n: number }[];
  flows: { flow: string; started: number; finished: number; rate: number | null }[];
  errors: { screen: string; error_code: string; n: number }[];
  help: { screen: string; n: number }[];
}

function Table<T>({ title, empty, cols, rows }: { title: string; empty: string; cols: [string, (r: T) => string | number][]; rows: T[] }) {
  return (
    <section>
      <h2>{title}</h2>
      {rows.length === 0 ? <p className="muted">{empty}</p> : (
        <table className="admin-table">
          <thead><tr>{cols.map(([h]) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>{cols.map(([h, f]) => <td key={h} data-label={h}>{f(r)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const seconds = (s: number | null): string => (s === null ? '-' : s >= 60 ? `${Math.floor(s / 60)}분 ${s % 60}초` : `${s}초`);

export function UsagePage({ lost }: { lost: Lost }) {
  const [days, setDays] = useState<1 | 7 | 30>(7);
  const [office, setOffice] = useState('');
  const [hideTest, setHideTest] = useHideTest();
  const list = useLoad<{ offices: OfficeRow[] }>('/api/admin/offices', lost);
  const url = `/api/admin/usage?days=${days}&hide_test=${hideTest ? 1 : 0}${office ? `&office=${office}` : ''}`;
  const { data, error } = useLoad<UsageReport>(url, lost);
  const offices = (list.data?.offices ?? []).filter((o) => !hideTest || !o.is_test);
  return (
    <>
      <h1>사용 현황</h1>
      <div className="row">
        {([[1, '오늘'], [7, '7일'], [30, '30일']] as const).map(([d, label]) => (
          <Button key={d} primary={days === d} onClick={() => setDays(d)}>{label}</Button>
        ))}
      </div>
      <div className="row">
        <select value={office} onChange={(e) => setOffice(e.target.value)} aria-label="사무소">
          <option value="">전체 사무소</option>
          {offices.map((o) => <option key={o.office_id} value={o.office_id}>{o.name ?? bizNoText(o.biz_no)}</option>)}
        </select>
      </div>
      <Check label="시험 사무소 빼기" checked={hideTest} onChange={setHideTest} />
      <Message text={error} />
      {data && (
        <>
          <Table title="화면별 열린 횟수와 머문 시간" empty="기록이 없어요." rows={data.screens}
            cols={[['화면', (r) => r.screen], ['열린 횟수', (r) => r.opens], ['평균 머문 시간', (r) => seconds(r.avg_seconds)]]} />
          <Table title="많이 누른 버튼" empty="기록이 없어요." rows={data.buttons}
            cols={[['화면', (r) => r.screen], ['버튼', (r) => r.target], ['누른 횟수', (r) => r.n]]} />
          <Table title="흐름별 끝까지 간 비율" empty="기록이 없어요." rows={data.flows}
            cols={[['흐름', (r) => r.flow], ['시작', (r) => r.started], ['끝냄', (r) => r.finished], ['끝까지 간 비율', (r) => (r.rate === null ? '-' : `${r.rate}%`)]]} />
          <Table title="오류가 많이 난 화면" empty="오류가 없어요." rows={data.errors}
            cols={[['화면', (r) => r.screen], ['오류', (r) => r.error_code], ['횟수', (r) => r.n]]} />
          <Table title="문의하기를 많이 누른 화면" empty="기록이 없어요." rows={data.help}
            cols={[['화면', (r) => r.screen], ['누른 횟수', (r) => r.n]]} />
        </>
      )}
    </>
  );
}

interface Recipient {
  recipient_id: string;
  name: string | null;
  chat_id: string | null;
  kinds: string[];
  status: '연결 대기' | '연결됨' | '끔';
  connected_at: string | null;
}

function RecipientBox({ r, kinds, lost, reload }: { r: Recipient; kinds: string[]; lost: Lost; reload: () => void }) {
  const [picked, setPicked] = useState<string[]>(r.kinds);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const call = async (path: string, body?: Record<string, unknown>, done?: string) => {
    setError(null);
    setNote(null);
    const res = await api('POST', `/api/admin/alerts/${r.recipient_id}${path}`, body);
    if (!res.ok) return res.status === 401 ? lost() : setError(res.message);
    if (done) setNote(done);
    reload();
  };
  if (r.status === '연결 대기') {
    return <div className="box"><h2>{r.name ?? '이름 없음'}</h2><p className="muted">연결을 기다리는 중이에요. 링크를 눌러 봇에게 시작을 보내면 연결돼요.</p></div>;
  }
  const changed = picked.length !== r.kinds.length || picked.some((k) => !r.kinds.includes(k));
  return (
    <div className="box">
      <h2>{r.name ?? '이름 없음'} {r.status === '끔' && <span className="muted">(끔)</span>}</h2>
      {r.status === '연결됨' ? (
        <>
          {kinds.map((k) => (
            <Check key={k} label={k} checked={picked.includes(k)} onChange={(v) => setPicked(v ? [...picked, k] : picked.filter((x) => x !== k))} />
          ))}
          <Message text={error} />
          <Message text={note} kind="info" />
          <Button primary disabled={!changed} onClick={() => void call('', { kinds: picked }, '저장했어요.')}>저장</Button>
          <Button onClick={() => void call('/off')}>알림 끄기</Button>
        </>
      ) : (
        <Button onClick={() => void call('/on')}>다시 켜기</Button>
      )}
    </div>
  );
}

export function AlertsPage({ lost }: { lost: Lost }) {
  const { data, error, reload } = useLoad<{ kinds: string[]; recipients: Recipient[]; bot: string | null }>('/api/admin/alerts', lost);
  const [name, setName] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  // 연결 링크를 연 동안에는 연결됐는지 10초마다 다시 본다
  useEffect(() => {
    if (!link) return;
    const t = window.setInterval(reload, 10_000);
    return () => window.clearInterval(t);
  }, [link, reload]);

  const add = async () => {
    setLinkError(null);
    const r = await api<{ url: string }>('POST', '/api/admin/alerts/link', { name });
    if (!r.ok) return r.status === 401 ? lost() : setLinkError(r.message);
    setLink(r.url);
    setName('');
    reload();
  };

  return (
    <>
      <h1>텔레그램</h1>
      <Message text={error} />
      {data && data.recipients.filter((r) => r.status !== '연결 대기').length === 0 && (
        <p>아직 연결한 사람이 없어요. 지금은 서버에 넣어 둔 대표님 텔레그램으로 알림이 가요.</p>
      )}
      {data?.recipients.map((r) => <RecipientBox key={`${r.recipient_id}:${r.kinds.join(',')}:${r.status}`} r={r} kinds={data.kinds} lost={lost} reload={reload} />)}
      <section className="box">
        <h2>받는 사람 더하기</h2>
        <Field label="이름 (예: CS 담당)" value={name} onChange={setName} />
        <Button primary onClick={() => void add()}>연결 링크 만들기</Button>
        <Message text={linkError} />
        {link && (
          <>
            <p>받을 사람이 휴대폰에서 이 링크를 누르고 봇에게 시작을 보내면 연결돼요. 1시간 동안 쓸 수 있어요.</p>
            <p style={{ wordBreak: 'break-all' }}><a href={link} target="_blank" rel="noreferrer">{link}</a></p>
            <Button onClick={() => void navigator.clipboard?.writeText(link)}>링크 복사</Button>
          </>
        )}
      </section>
    </>
  );
}
